/*
 * LOCAL-ONLY: the phone-width relocation layer (relocation parity, 2026-10-30), Obyrus' bar and sheet in the browser:
 *  - while relocation is open, a selection bar in the composer's place (RelocationComposerSwitch): what to select
 *    next or how many are selected, the one close action "Close relocation mode", progress / result / error lines,
 *    Reset selection (or Stop after this batch) and Relocate, which opens the relocate panel;
 *  - the panel itself as an overlay; tapping outside it or the back gesture returns to the bar, and back on the bar
 *    closes relocation (useMobileSelectModeHistoryDismiss).
 * The panel overlay is rendered through a portal into document.body: the channel view's .contentGrid sets
 * `contain: layout` and `overflow: hidden`, which would trap a fixed-position overlay.
 *
 * Lines to check on upstream merge: none — exclude this file entirely from any upstream sync.
 */

// SPDX-License-Identifier: AGPL-3.0-or-later

import styles from '@app/features/channel/components/channel_view/MobileSelectModeOverlay.module.css';
import {SelectModePanel} from '@app/features/channel/components/channel_view/SelectModePanel';
import type {Channel} from '@app/features/channel/models/Channel';
import {movingBackText, progressText, RelocationStrings} from '@app/features/channel/state/RelocationStrings';
import SelectMode from '@app/features/channel/state/SelectMode';
import {
	closeRelocationAtPhoneWidth,
	dismissRelocatePanel,
	useMobileSelectModeHistoryDismiss,
} from '@app/features/channel/utils/MobileSelectModeHistory';
import {relocationResultText, selectionStatusText} from '@app/features/channel/utils/RelocationLabels';
import type {Guild} from '@app/features/guild/models/Guild';
import {Button} from '@app/features/ui/button/Button';
import MobileLayout from '@app/features/ui/state/MobileLayout';
import {XIcon} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {createPortal} from 'react-dom';

const S = RelocationStrings;

export const MobileRelocationLayer = observer(function MobileRelocationLayer({
	guild,
	channel,
}: {
	guild?: Guild | null;
	channel: Channel;
}) {
	useMobileSelectModeHistoryDismiss(channel.id, true);
	if (typeof document === 'undefined' || !SelectMode.isActive || SelectMode.channelId !== channel.id) {
		return null;
	}
	if (SelectMode.isPanelOpen) {
		return createPortal(
			<>
				{/* Tapping outside the panel returns to the bar, like dismissing Obyrus' sheet. */}
				<div
					className={styles.backdrop}
					aria-hidden="true"
					onClick={() => dismissRelocatePanel(channel.id)}
					data-flx="channel.channel-view.mobile-relocation-layer.backdrop"
				/>
				<div
					className={clsx(styles.panel, styles.panelOpen)}
					data-flx="channel.channel-view.mobile-relocation-layer.panel"
				>
					<SelectModePanel guild={guild} channel={channel} />
				</div>
			</>,
			document.body,
		);
	}
	return null;
});

/**
 * The phone-width selection bar, in the composer's place while relocation is open in this channel (Obyrus'
 * RelocationComposerSwitch): the composer stays mounted, hidden, so its draft is kept, and the bar never covers the
 * newest messages.
 */
export const RelocationComposerSwitch = observer(function RelocationComposerSwitch({
	channel,
	children,
}: {
	channel: Channel;
	children: React.ReactNode;
}) {
	const active = MobileLayout.enabled && SelectMode.isActive && SelectMode.channelId === channel.id;
	return (
		<>
			<div hidden={active} style={active ? {display: 'none'} : undefined}>
				{children}
			</div>
			{active && <RelocationBar channel={channel} />}
		</>
	);
});

const RelocationBar = observer(function RelocationBar({channel}: {channel: Channel}) {
	const progress = SelectMode.progress;
	const result = SelectMode.result;
	return (
		<div className={styles.bar} data-flx="channel.channel-view.mobile-relocation-layer.bar">
			<div className={styles.barRow}>
				<span className={styles.barStatus} data-flx="channel.channel-view.mobile-relocation-layer.status">
					{selectionStatusText(channel.id)}
				</span>
				<Button
					type="button"
					variant="secondary"
					small
					square
					icon={<XIcon size={18} weight="bold" />}
					aria-label={S.closeRelocation}
					disabled={SelectMode.busy}
					onClick={() => closeRelocationAtPhoneWidth(channel.id)}
					data-flx="channel.channel-view.mobile-relocation-layer.close"
				/>
			</div>
			{progress != null && (
				<span className={styles.barLine} data-flx="channel.channel-view.mobile-relocation-layer.progress">
					{progress.undo ? movingBackText(progress.moved) : progressText(progress.moved, progress.total)}
				</span>
			)}
			{progress == null && result != null && (
				<span className={styles.barLine} data-flx="channel.channel-view.mobile-relocation-layer.result">
					{relocationResultText(result)}
				</span>
			)}
			{SelectMode.lastError != null && (
				<span className={styles.barError} data-flx="channel.channel-view.mobile-relocation-layer.error">
					{SelectMode.lastError}
				</span>
			)}
			<div className={styles.barButtons}>
				{progress != null && !progress.undo ? (
					<Button
						type="button"
						variant="secondary"
						disabled={SelectMode.stopRequested}
						onClick={SelectMode.requestStop}
						data-flx="channel.channel-view.mobile-relocation-layer.stop"
					>
						{S.stopAfterBatch}
					</Button>
				) : (
					<Button
						type="button"
						variant="secondary"
						disabled={(SelectMode.anchorId == null && SelectMode.headId == null) || SelectMode.busy}
						onClick={SelectMode.reset}
						data-flx="channel.channel-view.mobile-relocation-layer.reset"
					>
						{S.resetSelection}
					</Button>
				)}
				<Button
					type="button"
					variant="primary"
					onClick={SelectMode.openPanel}
					data-flx="channel.channel-view.mobile-relocation-layer.relocate"
				>
					{S.relocate}
				</Button>
			</div>
		</div>
	);
});
